import { useState, useEffect, useCallback } from 'react';
import { Email } from '@/types/email';
import { 
  getOrCreateInbox, 
  generateNewInbox, 
  fetchEmails, 
  markEmailAsRead, 
  deleteEmailById,
  sendTestEmail,
  getTimeRemaining,
  InboxSession 
} from '@/lib/emailGenerator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { notifyNewEmail } from '@/lib/notificationSound';

export const useInbox = () => {
  const [session, setSession] = useState<InboxSession | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({ hours: 24, minutes: 0, expired: false });
  const { toast } = useToast();

  // Transform backend email to frontend Email type
  const transformEmail = (backendEmail: any): Email => ({
    id: backendEmail.id,
    from: backendEmail.from_address,
    fromName: backendEmail.from_name || backendEmail.from_address.split('@')[0],
    subject: backendEmail.subject,
    preview: backendEmail.preview || '',
    body: backendEmail.body || '',
    bodyHtml: backendEmail.body_html,
    receivedAt: new Date(backendEmail.received_at),
    isRead: backendEmail.is_read,
    hasOTP: backendEmail.has_otp,
    otp: backendEmail.otp,
  });

  // Initialize session
  useEffect(() => {
    const initInbox = async () => {
      try {
        const inbox = await getOrCreateInbox();
        setSession(inbox);
        
        // Fetch initial emails
        const emailsData = await fetchEmails(inbox.token);
        setEmails(emailsData.map(transformEmail));
      } catch (error) {
        console.error('Error initializing inbox:', error);
        toast({
          title: 'Error',
          description: 'Failed to initialize inbox. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    initInbox();
  }, [toast]);

  // Subscribe to realtime email updates - filtered by inbox_id
  useEffect(() => {
    if (!session?.inboxId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('emails-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'emails',
            filter: `inbox_id=eq.${session.inboxId}`,
          },
          async (payload) => {
            console.log('New email received for inbox');
            // Refresh emails to get the new one
            try {
              const emailsData = await fetchEmails(session.token);
              setEmails(emailsData.map(transformEmail));
              
              // Trigger notification sound and haptic feedback
              await notifyNewEmail();
              
              toast({
                title: '📬 New Email',
                description: 'You have received a new email!',
              });
            } catch (error) {
              console.error('Error fetching new emails:', error);
            }
          }
        )
        .subscribe((status, err) => {
          if (err) {
            console.warn('Realtime subscription error (non-critical):', err.message);
          }
        });
    } catch (error) {
      console.warn('Failed to setup realtime subscription:', error);
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [session?.inboxId, session?.token, toast]);

  // Update time remaining
  useEffect(() => {
    if (!session) return;

    const updateTime = () => {
      const remaining = getTimeRemaining(session.expiresAt);
      setTimeRemaining(remaining);

      if (remaining.expired) {
        // Auto-generate new inbox when expired
        generateNew();
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, [session]);

  // Refresh inbox
  const refreshInbox = useCallback(async () => {
    if (!session) return;
    
    setIsLoading(true);
    try {
      const emailsData = await fetchEmails(session.token);
      setEmails(emailsData.map(transformEmail));
    } catch (error) {
      console.error('Error refreshing inbox:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh inbox.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, toast]);

  // Generate new email address
  const generateNew = useCallback(async () => {
    setIsLoading(true);
    try {
      const newInbox = await generateNewInbox();
      setSession(newInbox);
      setEmails([]);
      setSelectedEmail(null);
      toast({
        title: 'New Address',
        description: 'Your new private email address is ready!',
      });
    } catch (error) {
      console.error('Error generating new inbox:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate new address.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Mark email as read
  const markAsRead = useCallback(async (emailId: string) => {
    if (!session) return;
    
    try {
      await markEmailAsRead(session.token, emailId);
      setEmails((prev) =>
        prev.map((email) =>
          email.id === emailId ? { ...email, isRead: true } : email
        )
      );
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  }, [session]);

  // Delete email
  const deleteEmail = useCallback(async (emailId: string) => {
    if (!session) return;
    
    try {
      await deleteEmailById(session.token, emailId);
      setEmails((prev) => prev.filter((email) => email.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      toast({
        title: 'Deleted',
        description: 'Email deleted successfully.',
      });
    } catch (error) {
      console.error('Error deleting email:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete email.',
        variant: 'destructive',
      });
    }
  }, [session, selectedEmail, toast]);

  // Select email
  const selectEmail = useCallback((email: Email | null) => {
    setSelectedEmail(email);
    if (email && !email.isRead) {
      markAsRead(email.id);
    }
  }, [markAsRead]);

  // Send test email
  const sendTest = useCallback(async () => {
    if (!session) return;
    
    try {
      await sendTestEmail(session.token);
      // Refresh to show the new email
      await refreshInbox();
      toast({
        title: '📬 Test Email Sent',
        description: 'A sample email has been added to your inbox!',
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send test email.',
        variant: 'destructive',
      });
    }
  }, [session, refreshInbox, toast]);

  const unreadCount = emails.filter((e) => !e.isRead).length;

  return {
    session,
    emails,
    selectedEmail,
    isLoading,
    timeRemaining,
    unreadCount,
    refreshInbox,
    generateNew,
    markAsRead,
    deleteEmail,
    selectEmail,
    sendTest,
  };
};
